import type { AccountOption } from "@app/components/account-combobox";
import type { StakeholderOption } from "@app/components/stakeholder-combobox";
import TxnTablePanel, {
  TXN_PANEL_DIALOG_CLASS,
  useTxnSort,
} from "@app/components/txn-table-panel";
import { useTxnBulkActions } from "@app/components/txn-table-panel/use-txn-bulk-actions";
import { useTxnPanelRefresh } from "@app/components/txn-table-panel/use-txn-panel-refresh";
import { getPerspectiveId } from "@app/lib/perspective";
import * as Dialog from "@radix-ui/react-dialog";
import { Suspense, useState } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import type { cacTxnsDrawer_Query } from "@app/queries/cacTxnsDrawer_Query.graphql";

const drawerQuery = graphql`
  query cacTxnsDrawer_Query(
    $txnIds: [ID!]
    $perspectiveId: ID
    $sort: TxnSortEnum
    $sortDirection: String
  ) {
    transactions(
      txnIds: $txnIds
      perspectiveId: $perspectiveId
      sort: $sort
      sortDirection: $sortDirection
    ) {
      totalCount
      totalAmount
      limit
      txns {
        txn {
          id
        }
        md5
        postingDate
        description
        amount
        kind
        accountNumber
        internal
        classification
        counterparty {
          name
        }
        category
        expenseNature {
          id
          name
        }
      }
    }
    allStakeholders {
      id
      name
    }
    bankAccounts {
      id
      name
      nickname
      last4
    }
    expenseNatures(perspectiveId: $perspectiveId) {
      id
      name
    }
  }
`;

// The plumbing nodes (Root + per-account BASE) are never valid counterparties.
const isRealStakeholder = (s: { name: string }) =>
  !s.name.startsWith("&") &&
  !s.name.toLowerCase().includes("root stakeholder") &&
  s.name !== "_root";

// The CAC subcategory drill: an explicit set of acquisition-spend txns (the leaf
// rows of one cost-centre subcategory) opened on the shared editable shell, with
// the full bulk-action set. Everything re-derives server-side, so onChanged()
// refreshes the month panel's spend totals afterwards.
export default function CacTxnsDrawer({
  title,
  txnIds,
  onClose,
  onChanged,
}: {
  title: string;
  txnIds: readonly string[];
  onClose: () => void;
  onChanged: () => void;
}) {
  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/20 z-40" />
        <Dialog.Content className={TXN_PANEL_DIALOG_CLASS}>
          <Suspense fallback={<div className="p-6 text-sm text-gray-400">Loading transactions…</div>}>
            <DrawerContent title={title} txnIds={txnIds} onClose={onClose} onChanged={onChanged} />
          </Suspense>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function DrawerContent({
  title,
  txnIds,
  onClose,
  onChanged,
}: {
  title: string;
  txnIds: readonly string[];
  onClose: () => void;
  onChanged: () => void;
}) {
  const { refreshing, refresh, queryOptions } = useTxnPanelRefresh();
  const { sort, direction, onSort, sorting, sortVars } = useTxnSort();

  const data = useLazyLoadQuery<cacTxnsDrawer_Query>(
    drawerQuery,
    { txnIds: [...txnIds], perspectiveId: getPerspectiveId() ?? undefined, ...sortVars },
    queryOptions
  );
  const rows = data.transactions.txns;
  const stakeholderOptions: StakeholderOption[] = data.allStakeholders.filter(isRealStakeholder);
  const accountOptions: AccountOption[] = data.bankAccounts.map((a) => ({
    id: a.id,
    name: a.name,
    nickname: a.nickname,
    last4: a.last4,
  }));

  const { busy, renderBulkActions, dialogs } = useTxnBulkActions({
    enabled: ["setCounterparty", "createRule", "markInternal", "reclassify", "tagNature"],
    perspectiveId: getPerspectiveId(),
    onChanged: () => {
      refresh();
      onChanged();
    },
    rows,
    stakeholderOptions,
    accountOptions,
    natureOptions: data.expenseNatures,
  });

  // The /exports/transactions endpoint filters by the same args as the resolver,
  // so the explicit txn-id set carries through as a comma-joined param.
  const exportParams: Record<string, string> = {
    txn_ids: [...txnIds].join(","),
    sort: sortVars.sort,
    sort_direction: sortVars.sortDirection,
  };
  const perspectiveId = getPerspectiveId();
  if (perspectiveId) exportParams.perspective_id = perspectiveId;

  return (
    <>
      <TxnTablePanel
        title={title}
        totalCount={data.transactions.totalCount}
        headerTotalDollars={data.transactions.totalAmount}
        rows={rows}
        emptyLabel="No transactions in this subcategory."
        sort={sort}
        direction={direction}
        onSort={onSort} sorting={sorting}
        exportParams={exportParams}
        busy={busy || refreshing}
        onClose={onClose}
        renderBulkActions={renderBulkActions}
      />
      {dialogs}
    </>
  );
}
